import { Link } from "react-router-dom";
import {
  CarProfile,
  Clock,
  EnvelopeSimple,
  MapPin,
  Phone,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader } from "@/components/common";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Venue() {
  const { data: info, loading } = useGet("/venue-info");
  if (loading) return <Loading label="Loading venue info" />;

  return (
    <div data-testid="venue-page">
      <PageHeader
        eyebrow="Good to Know"
        title="Venue & FAQs"
        description="Key venue information and answers to the questions we are asked most."
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <h2 className="font-heading text-3xl text-primary font-light">{info?.name}</h2>
            <p className="mt-4 flex items-start gap-3 text-sm text-muted-foreground" data-testid="venue-directions">
              <CarProfile size={20} className="shrink-0 text-secondary" />
              {info?.directions}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <p className="eyebrow">Before your wedding day</p>
            <h3 className="mt-2 font-heading text-2xl text-primary">Site Visitor Policy</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Every supplier, contractor and vendor working on-site must read and sign the Site
              Visitor Policy before your event date. Download it from your Forms page and email
              signed copies to{" "}
              <a className="text-secondary hover:text-primary" href={`mailto:${info?.email}`}>
                {info?.email}
              </a>
              .
            </p>
            <Link
              to="/forms"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm text-primary transition-colors duration-200 hover:bg-muted"
              data-testid="venue-site-visitor-policy-link"
            >
              <ShieldCheck size={16} /> Open Forms
            </Link>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-4">
          <p className="eyebrow">Contact</p>
          <p className="flex items-start gap-3 text-sm" data-testid="venue-address">
            <MapPin size={20} className="text-secondary shrink-0" /> {info?.address}
          </p>
          <p className="flex items-center gap-3 text-sm" data-testid="venue-phone">
            <Phone size={20} className="text-secondary shrink-0" /> {info?.phone}
          </p>
          <p className="flex items-center gap-3 text-sm" data-testid="venue-email">
            <EnvelopeSimple size={20} className="text-secondary shrink-0" />
            <a className="hover:text-secondary" href={`mailto:${info?.email}`}>{info?.email}</a>
          </p>
          <div className="border-t border-border pt-4 space-y-3">
            <p className="flex items-start gap-3 text-sm" data-testid="venue-office-hours">
              <Clock size={20} className="text-secondary shrink-0" /> {info?.office_hours}
            </p>
            <p className="flex items-start gap-3 text-sm" data-testid="venue-cellar-hours">
              <Clock size={20} className="text-secondary shrink-0" /> {info?.hours}
            </p>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
        <p className="eyebrow mb-4">
          Frequently asked questions ({(info?.faqs || []).length})
        </p>
        <Accordion type="single" collapsible className="w-full">
          {(info?.faqs || []).map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-heading text-lg text-primary hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
